// Scrubs personal data from Sentry events before they leave the app.
// Privacy decision (docs/privacy/privacy-audit.md): the signed-in user is identified
// by id and e-mail (declared in the privacy policy); username and IP are dropped.
// E-mail addresses that show up in free text (message, `extra`, contexts, tags,
// breadcrumbs, request) are still redacted, because they usually come from request
// bodies that also carry OTP codes. The scrub runs centrally in every beforeSend
// instead of relying on each call site.

import type { Event } from "@sentry/nextjs";

const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const REDACTED = "[email]";
const MAX_DEPTH = 8;

function redactValue(value: unknown, depth: number): unknown {
  if (typeof value === "string") {
    return value.replace(EMAIL_PATTERN, REDACTED);
  }
  if (depth >= MAX_DEPTH || value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1));
  }
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    result[key] = redactValue(item, depth + 1);
  }
  return result;
}

function redact<T>(value: T): T {
  return redactValue(value, 0) as T;
}

export function scrubPii<T extends Event>(event: T): T {
  if (event.user) {
    // Keep the id and the e-mail that identify the signed-in person; drop username and IP.
    event.user = {
      ...(event.user.id !== undefined ? { id: event.user.id } : {}),
      ...(event.user.email ? { email: event.user.email } : {}),
    };
  }
  if (event.message) event.message = redact(event.message);
  if (event.extra) event.extra = redact(event.extra);
  if (event.contexts) event.contexts = redact(event.contexts);
  if (event.tags) event.tags = redact(event.tags);
  if (event.breadcrumbs) event.breadcrumbs = redact(event.breadcrumbs);
  if (event.request) event.request = redact(event.request);
  event.exception?.values?.forEach((exception) => {
    if (exception.value) exception.value = redact(exception.value);
  });
  return event;
}
